import {
  CompactSelection,
  type GridSelection,
  type Item,
  type Rectangle,
} from "@glideapps/glide-data-grid";
import {
  detectDesktopPlatform,
  type DesktopPlatform,
} from "@/core/utils/desktop-platform";

type MultiSelectModifierEvent = Pick<MouseEvent, "ctrlKey" | "metaKey">;

export const matchesMultiSelectModifier = (
  event: MultiSelectModifierEvent,
  platform: DesktopPlatform = detectDesktopPlatform(),
) =>
  platform === "mac"
    ? event.metaKey && !event.ctrlKey
    : event.ctrlKey && !event.metaKey;

const cellRectangle = ([col, row]: Item): Rectangle => ({
  x: col,
  y: row,
  width: 1,
  height: 1,
});

const rectangleContainsCell = (rectangle: Rectangle, [col, row]: Item) =>
  col >= rectangle.x &&
  col < rectangle.x + rectangle.width &&
  row >= rectangle.y &&
  row < rectangle.y + rectangle.height;

const removeCellFromRectangle = (
  rectangle: Rectangle,
  cell: Item,
): Rectangle[] => {
  if (!rectangleContainsCell(rectangle, cell)) return [rectangle];

  const [col, row] = cell;
  const rectangles: Rectangle[] = [];
  const topHeight = row - rectangle.y;
  const bottomY = row + 1;
  const bottomHeight = rectangle.y + rectangle.height - bottomY;
  const leftWidth = col - rectangle.x;
  const rightX = col + 1;
  const rightWidth = rectangle.x + rectangle.width - rightX;

  if (topHeight > 0) {
    rectangles.push({ ...rectangle, height: topHeight });
  }
  if (bottomHeight > 0) {
    rectangles.push({
      ...rectangle,
      y: bottomY,
      height: bottomHeight,
    });
  }
  if (leftWidth > 0) {
    rectangles.push({
      x: rectangle.x,
      y: row,
      width: leftWidth,
      height: 1,
    });
  }
  if (rightWidth > 0) {
    rectangles.push({ x: rightX, y: row, width: rightWidth, height: 1 });
  }

  return rectangles;
};

const getSelectionRectangles = (selection: GridSelection): Rectangle[] => [
  ...(selection.current ? [selection.current.range] : []),
  ...(selection.current?.rangeStack ?? []),
];

export const getSelectedCellCoordinates = (
  selection: GridSelection,
  rowCount: number,
  columnCount: number,
): Item[] => {
  const coordinates = new Map<string, Item>();
  const addCoordinate = (col: number, row: number) => {
    if (col < 0 || col >= columnCount || row < 0 || row >= rowCount) return;
    coordinates.set(`${col}:${row}`, [col, row]);
  };

  getSelectionRectangles(selection).forEach(({ x, y, width, height }) => {
    for (let row = y; row < y + height; row += 1) {
      for (let col = x; col < x + width; col += 1) {
        addCoordinate(col, row);
      }
    }
  });

  selection.rows.toArray().forEach((row) => {
    for (let col = 0; col < columnCount; col += 1) {
      addCoordinate(col, row);
    }
  });
  selection.columns.toArray().forEach((col) => {
    for (let row = 0; row < rowCount; row += 1) {
      addCoordinate(col, row);
    }
  });

  return Array.from(coordinates.values());
};

export const toggleCellInSelection = (
  selection: GridSelection,
  cell: Item,
): GridSelection => {
  const rectangles = getSelectionRectangles(selection);
  const isSelected = rectangles.some((rectangle) =>
    rectangleContainsCell(rectangle, cell),
  );

  if (!isSelected) {
    return {
      current: {
        cell,
        range: cellRectangle(cell),
        rangeStack: rectangles,
      },
      columns: CompactSelection.empty(),
      rows: CompactSelection.empty(),
    };
  }

  const remainingRectangles = rectangles.flatMap((rectangle) =>
    removeCellFromRectangle(rectangle, cell),
  );
  const [currentRange, ...rangeStack] = remainingRectangles;
  if (!currentRange) {
    return {
      columns: CompactSelection.empty(),
      rows: CompactSelection.empty(),
    };
  }

  const previousActiveCell = selection.current?.cell;
  const activeCell =
    previousActiveCell &&
    rectangleContainsCell(currentRange, previousActiveCell)
      ? previousActiveCell
      : ([currentRange.x, currentRange.y] as const);

  return {
    current: {
      cell: activeCell,
      range: currentRange,
      rangeStack,
    },
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty(),
  };
};
